import React, { useContext, useMemo } from 'react';
import { Timestamp } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import {
  Button,
  Divider,
  Empty,
  Popconfirm,
  Spin,
  Typography,
  Tag,
} from 'antd';

import { UserContext } from '../../providers/UserContext';
import FlexCard from '../atoms/FlexCard';
import FlexCol from '../atoms/FlexCol';
import FlexBox from '../atoms/FlexBox';
import Icon from '../atoms/Icon';
import EmailAnalysisNotificationCard from './EmailAnalysisNotificationCard';
import { Notification, EmailAnalysisNotificationData } from '#types/System.types';
import { notificationChangeTypeColors, notificationChangeTypeLabels } from '#types/System.Parameters.types';

const { Text } = Typography;

type ViewType = 'Unread' | 'All';

interface NotificationsTableProps {
  view: ViewType;
}

const NotificationsTable: React.FC<NotificationsTableProps> = ({ view }) => {
  const {
    notificationBundles,
    toggleNotificationRead,
    deleteNotification
  } = useContext(UserContext);

  const unreadNotifications = notificationBundles?.unreadNotifications;
  const readNotifications = notificationBundles?.readNotifications;
  const notificationEntryByEventId = notificationBundles?.notificationEntryByEventId;

  const notifications = useMemo(() => {
    if (!unreadNotifications && !readNotifications) return null;

    if (view === 'Unread') {
      return [...(unreadNotifications || [])].sort((a, b) =>
        (b.timestamp as Timestamp).toMillis() - (a.timestamp as Timestamp).toMillis()
      );
    }

    const allNotifications = [
      ...(unreadNotifications || []),
      ...(readNotifications || [])
    ].sort((a, b) =>
      (b.timestamp as Timestamp).toMillis() - (a.timestamp as Timestamp).toMillis()
    );

    return allNotifications;
  }, [unreadNotifications, readNotifications, view]);

  if (!notifications) {
    return <Spin />;
  }

  if (notifications.length === 0) {
    return (
      <FlexCard style={{ width: '100%' }} alignCenter>
        <Empty
          description={`No ${view === 'Unread' ? 'unread' : ''} notifications`}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          styles={{ image: { height: 30 } }}
          style={{ margin: 5 }}
        />
      </FlexCard>
    );
  }

  const renderNotification = (notification: Notification, index: number) => {
    const {
      notificationEvent,
      timestamp
    } = notification;
    const {
      docId,
      relatedUri,
      message,
      changeType,
      meta,
      data
    } = notificationEvent;
    const isRead = notificationEntryByEventId?.get(docId ?? '')?.status === 'read';
    const isApproved = changeType === 'EMAIL_ANALYSIS' && 
      (data as EmailAnalysisNotificationData)?.approvedBy !== undefined;
    const isDismissed = changeType === 'EMAIL_ANALYSIS' && 
      (data as EmailAnalysisNotificationData)?.dismissedBy !== undefined;

    if (changeType === 'EMAIL_ANALYSIS' && data) {
      return (
        <div key={docId} style={{ width: '100%' }}>
          <EmailAnalysisNotificationCard
            notification={notification}
            isApproved={isApproved}
          />
          {index < notifications.length - 1 && <Divider style={{ margin: 0 }} />}
        </div>
      );
    }

    const {
      userName,
      userId
    } = meta || {};
    const userIdentifier = userName || userId || '';

    return (
      <div key={docId} style={{ width: '100%' }}>
        <FlexBox stretch gap={0} alignStart>
          <Button
            type="text"
            style={{
              flex: 1,
              minWidth: 0,
              height: 'auto',
              paddingTop: 5,
              paddingBottom: 10,
              borderRadius: 0,
              whiteSpace: 'normal',
              textAlign: 'left',
              ...(isDismissed && {
                backgroundColor: '#fff7e6',
                borderLeft: '3px solid #faad14'
              })
            }}
            onClick={(e) => {
              if (!(e.target as HTMLElement).closest('a')) {
                toggleNotificationRead(notification);
              }
            }}
          >
            {isRead ?
              <Icon.CheckCircle /> :
              <Icon.Circle />
            }
            <FlexCol 
              stretch 
              gap={0} 
              style={{ 
                marginLeft: 10, 
                minWidth: 0,
                opacity: isRead ? 0.6 : 1 
              }}
            >
              <Text
                delete={isRead}
                type={isRead ? "secondary" : undefined}
                style={{ overflowWrap: 'anywhere' }}
              >
                {relatedUri ? (
                  <Link to={relatedUri}>
                    {message}
                  </Link>
                ) : (
                  <>
                    {message}
                  </>
                )}
                &nbsp;by {userIdentifier}
              </Text>
              <Text type="secondary">
                <Tag color={notificationChangeTypeColors[changeType]}>
                  {notificationChangeTypeLabels[changeType] || changeType}
                </Tag>
                {isDismissed && (
                  <Tag color="warning">Dismissed</Tag>
                )}
                {(timestamp as Timestamp).toDate().toLocaleString()}
              </Text>
            </FlexCol>
          </Button>
          <Popconfirm
            title="Delete notification?"
            description={`Are you sure you want to delete this notification?`}
            onConfirm={() => deleteNotification(notification)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true, 'aria-label': 'Confirm delete notification' }}
            cancelButtonProps={{ 'aria-label': 'Cancel delete notification' }}
          >
            <Button
              type="text"
              aria-label={`Delete notification ${message}`}
              icon={<Icon.Trash2 />}
            />
          </Popconfirm>
        </FlexBox>
        {index < notifications.length - 1 && <Divider style={{ margin: 0 }} />}
      </div>
    );
  };

  return (
    <FlexCard
      style={{ width: '100%' }}
      gap={0}
    >
      {notifications.map((notification, index) => 
        renderNotification(notification, index)
      )}
    </FlexCard>
  );
};

export default NotificationsTable; 